/**
 * AppShell — layout principal post-login.
 *
 * ┌──────────────────────────────────────────────────────┐
 * │ TopBar (branding tenant + usuario + logout)          │
 * ├──────────┬───────────────────────────┬───────────────┤
 * │ LeftRail │      ChatWindow           │  RightRail    │
 * │(collapse)│  o WelcomeScreen vacío    │ (collapsable) │
 * └──────────┴───────────────────────────┴───────────────┘
 *
 * R-PT-02: branding data-driven desde descriptor.tenant.branding.
 * R-PT-01: sin lógica de negocio — solo routing y layout.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { PanelLeft, PanelRight, Settings, Check, TriangleAlert } from 'lucide-react'
import { useSession } from '../context/SessionContext.jsx'
import { deleteChat } from '../api/client.js'
import LeftRail   from './LeftRail.jsx'
import RightRail  from './RightRail.jsx'
import ChatWindow from './ChatWindow.jsx'
import AdminPanel from './AdminPanel.jsx'
import FileExplorer from './FileExplorer.jsx'
import ConfirmDeleteChatDialog from './ConfirmDeleteChatDialog.jsx'

// US-467: mensaje de UI por `code` del envelope de error del kernel para el
// flujo de borrado (propose → approve). Estable frente a cambios de texto del
// backend; sin match, cae al mensaje del error o a un genérico.
const DELETE_ERROR_MESSAGES = {
  forbidden:               'No tienes permiso para eliminar esta conversación.',
  chat_no_encontrado:      'La conversación ya no existe.',
  accion_no_encontrada:    'La acción de borrado ya no está disponible (expiró).',
  accion_no_permitida:     'Esta acción no está permitida.',
  parametros_invalidos:    'No se pudo preparar el borrado.',
  accion_estado_terminal:  'La conversación ya se eliminó o la acción expiró.',
  en_curso:                'El borrado ya está en curso.',
}
function deleteErrorMessage(err) {
  return DELETE_ERROR_MESSAGES[err?.code]
    ?? err?.message
    ?? 'No se pudo eliminar la conversación. Inténtalo de nuevo.'
}

// Ajustes (⚙) — SPEC §280-A.7 (decisión de Guo): el interruptor de tema NO es
// un switch prominente; va escondido detrás de un icono de ajustes que abre un
// popover pequeño (patrón reutilizado de `.agent-popover` de RightRail).
const THEME_OPTIONS = [
  { id: 'system', label: 'Sistema' },
  { id: 'light',  label: 'Claro' },
  { id: 'dark',   label: 'Oscuro' },
]

function SettingsMenu({ theme, setTheme }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e) { if (!ref.current?.contains(e.target)) setOpen(false) }
    function onKeyDown(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="topbar__settings" ref={ref}>
      <button
        className="topbar__settings-btn"
        onClick={() => setOpen(o => !o)}
        aria-label="Ajustes"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Ajustes"
      >
        <Settings size={18} strokeWidth={1.8} aria-hidden="true" />
      </button>
      {open && (
        <div className="settings-popover" role="menu" aria-label="Ajustes">
          <span className="settings-popover__label">Tema</span>
          {THEME_OPTIONS.map(opt => (
            <button
              key={opt.id}
              role="menuitemradio"
              aria-checked={theme === opt.id}
              className={'settings-popover__option' + (theme === opt.id ? ' settings-popover__option--active' : '')}
              onClick={() => { setTheme(opt.id); setOpen(false) }}
            >
              {opt.label}
              {theme === opt.id && <span className="settings-popover__check"><Check size={15} strokeWidth={2} aria-hidden="true" /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function TopBar({ tenant, user, viewLabel, theme, setTheme, onLogout, onToggleLeft, onToggleRight }) {
  const dominios = tenant?.dominios_activos ?? []

  return (
    <header className="topbar">
      <div className="topbar__left">
        <button
          className="topbar__rail-toggle"
          onClick={onToggleLeft}
          aria-label="Alternar navegación"
          title="Alternar rail izquierdo"
        >
          <PanelLeft size={18} strokeWidth={1.8} aria-hidden="true" />
        </button>
        <span className="topbar__view">{viewLabel}</span>
      </div>

      <div className="topbar__right">
        {tenant?.nombre && (
          <span className="topbar__tenant">{tenant.nombre}</span>
        )}
        {dominios.map(d => (
          <span key={d} className="topbar__domain">{d}</span>
        ))}
        {user && (
          <span className="topbar__user" title={user.rol}>
            {user.display_name}
          </span>
        )}
        <button
          className="topbar__rail-toggle"
          onClick={onToggleRight}
          aria-label="Alternar panel derecho"
          title="Alternar rail derecho"
        >
          <PanelRight size={18} strokeWidth={1.8} aria-hidden="true" />
        </button>
        <SettingsMenu theme={theme} setTheme={setTheme} />
        <button
          className="topbar__logout"
          onClick={onLogout}
          title="Cerrar sesión"
        >
          Salir
        </button>
      </div>
    </header>
  )
}


// US-68 (#202): aviso de incompatibilidad de contrato. Se pinta cuando el
// MAJOR de la `contract_version` del kernel no coincide con el que declara el
// front. Degradación visible (no crash silencioso): la app sigue renderizando
// lo que puede, pero avisa al usuario de que algo podría no funcionar.
function ContractBanner({ message }) {
  if (!message) return null
  return (
    <div className="contract-banner" role="alert">
      <span className="contract-banner__icon" aria-hidden="true"><TriangleAlert size={16} strokeWidth={1.8} /></span>
      <span className="contract-banner__text">{message}</span>
    </div>
  )
}


export default function AppShell() {
  const { tenant, user, chats, capabilities, logout, refresh, contractWarning, theme, setTheme } = useSession()

  // Chat "borrador": una conversación nueva nace ya con chat_id definitivo
  // (generado en cliente, patrón B4/US-62 — el kernel acepta el id en el
  // primer POST /messages). US-62: Pórtico no almacena memoria, así que el
  // borrador NO se guarda en ninguna caché local; entra en la lista real
  // cuando el kernel lo incluya en `descriptor.chats` del siguiente bootstrap.
  // El chat_id se fija al nacer para que la ventana no se remonte en mitad
  // del streaming.
  // Un borrador puede nacer "dirigido" a un agente concreto (US-02, al pinchar
  // un agente del rail) o sin dirigir (US-03, el kernel enruta a Anne). El
  // `agente_destino` (id) y `agente_nombre` viajan en la metadata del chat para
  // reflejar el interlocutor en cabecera y mandarlo en cada POST.
  const makeDraft = (agent = null) => ({
    chat_id: `c-${crypto.randomUUID()}`,
    titulo: agent ? `Conversación con ${agent.nombre}` : 'Nueva conversación',
    de_que_va: '',
    agentes_en_chat: [
      agent
        ? { nombre: agent.nombre, proposito: 'consulta directa' }
        : { nombre: 'Anne', proposito: 'orquestación' },
    ],
    agente_destino: agent?.id ?? null,
    agente_nombre: agent?.nombre ?? null,
    updated_at: new Date().toISOString(),
  })

  const [draft, setDraft]                   = useState(makeDraft)
  const [activeChatId, setActiveChatId]     = useState(null)
  const [leftCollapsed, setLeftCollapsed]   = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  // Vista del área central: 'chat' (por defecto) o una sección del menú
  // (data-driven, capabilities.menu). US-26 añade la vista 'admin'.
  const [activeView, setActiveView]         = useState('chat')
  // Fuentes citadas del hilo activo (RightRail «Fuentes citadas», #280) —
  // se derivan en ChatWindow de los `answer` recibidos; aquí solo se
  // reenvían al rail de contexto. Vacío por defecto: no se inventa el dato.
  const [chatSources, setChatSources]       = useState([])

  const effectiveChatId = activeChatId ?? draft.chat_id
  const isDraft = !chats.some(c => c.chat_id === effectiveChatId)
  const activeChat = chats.find(c => c.chat_id === effectiveChatId)
    ?? (draft.chat_id === effectiveChatId ? draft : null)

  // Etiqueta de la vista actual para la TopBar (Gabinete: la marca vive en el
  // rail; la barra muestra dónde estás). Data-driven por capabilities.menu.
  const menuItem = capabilities?.menu?.find(m => m.id === activeView)
  const viewLabel = activeView === 'chat'
    ? (isDraft ? 'Inicio' : (activeChat?.titulo ?? 'Conversación'))
    : (menuItem?.label ?? 'Inicio')

  // Seleccionar un chat vuelve siempre a la vista de conversación.
  const handleSelectChat = useCallback((chatId) => {
    setActiveChatId(chatId)
    setActiveView('chat')
  }, [])

  const handleNewChat = useCallback(() => {
    setDraft(makeDraft())
    setActiveChatId(null)
    setActiveView('chat')
  }, [])

  const handleSelectAgent = useCallback((agent) => {
    // US-02: abrir un chat nuevo ya dirigido a ese agente. El destino viaja en
    // la metadata del borrador -> ChatWindow lo usa en cabecera y en el POST.
    setDraft(makeDraft(agent))
    setActiveChatId(null)
    setActiveView('chat')
  }, [])

  // Navegar a una sección del menú (data-driven). Sólo 'admin' tiene vista
  // propia en esta fase; el resto de ítems habilitados caerían en el default.
  const handleNavigate = useCallback((viewId) => {
    setActiveView(viewId)
  }, [])

  // Primer mensaje del borrador: refresca en memoria el título del borrador
  // (derivado del mensaje) para que la cabecera lo refleje durante la sesión.
  // US-62: no se persiste nada — la conversación aparecerá en la lista real
  // cuando el kernel la incluya en `descriptor.chats` del siguiente bootstrap.
  const handleFirstSend = useCallback((content) => {
    const titulo = content.length > 48 ? `${content.slice(0, 48)}…` : content
    setDraft(d => ({ ...d, titulo }))
  }, [])

  // US-467 — borrar una conversación del historial. El rail pide borrar un chat;
  // se abre el diálogo de confirmación (evita borrados accidentales). Al
  // confirmar, se ejecuta el flujo gobernado (propose → approve, borrado real en
  // el store) y se refresca la lista DESDE EL STORE (re-bootstrap), no en local.
  const [chatToDelete, setChatToDelete] = useState(null)  // chat pendiente de confirmar
  const [deleting, setDeleting]         = useState(false)  // borrado en vuelo
  const [deleteError, setDeleteError]   = useState(null)   // error del último intento

  const handleRequestDeleteChat = useCallback((chat) => {
    setDeleteError(null)
    setChatToDelete(chat)
  }, [])

  const handleCancelDelete = useCallback(() => {
    if (deleting) return          // no cancelar a mitad de un borrado en curso
    setChatToDelete(null)
    setDeleteError(null)
  }, [deleting])

  const handleConfirmDelete = useCallback(async () => {
    const chat = chatToDelete
    if (!chat) return
    setDeleting(true)
    setDeleteError(null)
    try {
      // Flujo gobernado de dos pasos (contrato US-467): propone `purgar_chat` y
      // aprueba; el borrado real lo hace el chat store R0 (no la caché local).
      await deleteChat(chat.chat_id)
      // Refrescar la lista DESDE EL STORE (US-62/US-65): el descriptor
      // rehidratado ya no incluye el chat borrado; no se edita solo en local.
      await refresh()
      // Si el chat borrado era el activo, volver a un borrador nuevo (su ventana
      // ya no tiene historial que mostrar).
      if (activeChatId === chat.chat_id) {
        setDraft(makeDraft())
        setActiveChatId(null)
        setActiveView('chat')
      }
      setChatToDelete(null)   // cerrar el diálogo
    } catch (err) {
      setDeleteError(deleteErrorMessage(err))
    } finally {
      setDeleting(false)
    }
  }, [chatToDelete, refresh, activeChatId])

  return (
    <div className="app-shell">
      <TopBar
        tenant={tenant}
        user={user}
        viewLabel={viewLabel}
        theme={theme}
        setTheme={setTheme}
        onLogout={logout}
        onToggleLeft={() => setLeftCollapsed(c => !c)}
        onToggleRight={() => setRightCollapsed(c => !c)}
      />

      <ContractBanner message={contractWarning} />

      <div className="app-body">
        <LeftRail
          collapsed={leftCollapsed}
          activeChatId={activeView === 'chat' ? effectiveChatId : null}
          activeView={activeView}
          onSelectChat={handleSelectChat}
          onNewChat={handleNewChat}
          onSelectAgent={handleSelectAgent}
          onNavigate={handleNavigate}
          onDeleteChat={handleRequestDeleteChat}
        />

        <main className="main-area">
          {activeView === 'admin' ? (
            <AdminPanel />
          ) : activeView === 'ficheros' ? (
            <FileExplorer />
          ) : (
            <ChatWindow
              key={effectiveChatId}
              chatId={effectiveChatId}
              chatMeta={activeChat}
              isDraft={isDraft}
              onFirstSend={handleFirstSend}
              onSourcesChange={setChatSources}
              onSelectChat={handleSelectChat}
              onSelectAgent={handleSelectAgent}
            />
          )}
        </main>

        {activeView === 'chat' && (
          <RightRail
            collapsed={rightCollapsed}
            activeChat={activeChat}
            sources={chatSources}
          />
        )}
      </div>

      {/* US-467: confirmación de borrado. Se monta solo cuando hay un chat
          pendiente; captura la aprobación humana explícita del irreversible. */}
      {chatToDelete && (
        <ConfirmDeleteChatDialog
          chat={chatToDelete}
          busy={deleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  )
}
